import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { DialogHelper } from "@core/helpers/dialog-helper";
import { NotificationService } from "@core/services/notification.service";
import { TradingService } from "@core/services/trading.service";
import { ToasterSeverities } from "@enums/toaster-severities";
import { LOAD_PENDING_NAV } from "@features/dashboard/store/dashboard.actions";
import * as tradingActions from "@features/trading/store/trading.actions";
import {
  SET_NAV_HISTORY,
  SET_TRADING_LOADING_STATUS,
} from "@features/trading/store/trading.actions";
import { FundWithRelated } from "@models/fund-with-related.model";
import { Nav } from "@models/nav.model";
import { NavPeriod } from "@models/nav-period.model";
import { TradeMoment } from "@models/trade-moment-model";
import { TradingRun } from "@models/trading-run.model";
import { Yieldu } from "@models/yieldu-model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  forkJoin,
  last,
  map,
  mergeMap,
  Observable,
  of,
  range,
  switchMap,
  takeWhile,
  timer,
} from "rxjs";

@Injectable()
export class TradingEffects {
  /** Delay before the first poll of the latest trade moment after a trade action. */
  private static readonly TRADE_MOMENT_POLL_START_INTERVAL = 2000;
  /** Upper bound the poll delay backs off to. */
  private static readonly TRADE_MOMENT_POLL_MAX_INTERVAL = 15000;
  /** Maximum number of polls before the trade moment is left to the background job. */
  private static readonly TRADE_MOMENT_POLL_ATTEMPTS = 12;

  loadFundList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_FUND_LIST),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
        return this.tradingService.getFundList$().pipe(
          switchMap((fundList: Array<FundWithRelated>): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of(tradingActions.SET_FUND_LIST({ fundList: fundList }));
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of();
          })
        );
      })
    );
  });

  loadLatestTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_LATEST_TRADING),
      mergeMap((actions: { definitive: boolean }): Observable<Action> => {
        return this.tradingService.getLatestTrading$(actions.definitive).pipe(
          switchMap((tradeMoment: TradeMoment): Observable<Action> => {
            return of(
              tradingActions.SET_LATEST_TRADING({ tradeMoment: tradeMoment })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  generateOrderBook$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.GENERATE_ORDER_BOOK),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.generateOrderBook$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "ORDER_BOOK_MAIL_SUCCESS"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Error,
              "ORDER_BOOK_MAIL_FAILED",
              response
            );
            return of();
          })
        );
      })
    );
  });

  updateTransactions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.UPDATE_TRANSACTIONS),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.updateTransactions$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "UPDATE_TRANSACTIONS_SUCCESS"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Error,
              "UPDATE_TRANSACTIONS_FAILED",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadTransactionsCount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_TRANSACTIONS_COUNT),
      concatMap((): Observable<Action> => {
        return this.tradingService.getTransactionsCount$().pipe(
          switchMap((transactions: number): Observable<Action> => {
            return of(
              tradingActions.SET_TRANSACTIONS_COUNT({
                transactions: transactions,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  postTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.POST_TRADING),
      mergeMap((action: { tradingCanceled?: boolean }): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        const definitive: boolean = action.tradingCanceled
          ? action.tradingCanceled
          : false;
        // The run before this request, so a failure of an older run is not mistaken for this one.
        return this.lastTradingRun$().pipe(
          concatMap((previousRun: TradingRun | null) =>
            this.tradingService
              .postTrading$(action.tradingCanceled)
              .pipe(map((): TradingRun | null => previousRun))
          ),
          // The endpoint only schedules a background job, so the trade moment still holds its
          // previous state when the call returns. Reading it once here would leave the panel
          // showing that previous state until the user refreshes the page manually.
          switchMap((previousRun: TradingRun | null) =>
            this.pollTradeMoment$(definitive, previousRun)
          ),
          switchMap(({ failedRun, tradeMoment }): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            if (failedRun) {
              // The job failed (TL-1195): say so, instead of spinning or claiming it runs on.
              this.notificationService.show(
                ToasterSeverities.Error,
                definitive ? "TRADING_DEFINITIVE_FAILED" : "TRADING_FAILED",
                failedRun.message ?? undefined
              );
              return of(
                tradingActions.SET_LATEST_TRADING({ tradeMoment: tradeMoment }),
                tradingActions.LOAD_TRADE_STATUS()
              );
            }
            if (tradeMoment?.definitive !== definitive) {
              this.notificationService.show(
                ToasterSeverities.Info,
                definitive
                  ? "TRADING_DEFINITIVE_BACKGROUND"
                  : "TRADING_BACKGROUND"
              );
            }
            return of(
              tradingActions.SET_LATEST_TRADING({ tradeMoment: tradeMoment })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            return of();
          })
        );
      })
    );
  });

  deleteTrading$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.DELETE_TRADING),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
            tradeSectionLoading: true,
          })
        );
        return this.tradingService.deleteTrading$().pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            this.notificationService.show(
              ToasterSeverities.Success,
              "TRADING_CANCELED"
            );
            return of(tradingActions.LOAD_LATEST_TRADING({ definitive: true }));
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_MOMENT_LOADING_STATUS({
                tradeSectionLoading: false,
              })
            );
            return of();
          })
        );
      })
    );
  });

  exportReport$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.EXPORT_REPORT),
      mergeMap(
        (action: {
          registerReport: boolean;
          dateStart?: Date;
          dateEnd?: Date;
        }): Observable<Action> => {
          return this.tradingService
            .getTradingReport$(
              action.registerReport,
              action.dateStart,
              action.dateEnd
            )
            .pipe(
              switchMap((): Observable<Action> => {
                if (action.registerReport) {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "CUMULATIVE_REGISTER_BACKGROUND"
                  );
                } else {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "COST_EXPORT_BACKGROUND"
                  );
                }
                return of();
              }),
              catchError((): Observable<Action> => {
                return of();
              })
            );
        }
      )
    );
  });

  loadNavHistory$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.GENERATE_NAV_HISTORY),
      mergeMap(
        (action: {
          selectedIds: Array<string>;
          dateStart: string | null;
          dateEnd: string | null;
        }): Observable<Action> => {
          this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
          return this.tradingService
            .getNavHistory$(
              action.selectedIds,
              action.dateStart,
              action.dateEnd
            )
            .pipe(
              switchMap((list: Array<NavPeriod>): Observable<Action> => {
                this.store.dispatch(SET_NAV_HISTORY({ navTable: list }));
                return of(
                  tradingActions.SET_TRADING_LOADING_STATUS({ loading: false })
                );
              }),
              catchError((): Observable<Action> => {
                this.store.dispatch(
                  SET_TRADING_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  savePendingNav$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.SAVE_PENDING_NAV),
      concatMap((action: { navValues: Array<Nav> }): Observable<Action> => {
        this.store.dispatch(
          tradingActions.SET_TRADING_LOADING_STATUS({ loading: true })
        );
        return this.tradingService.savePendingNav$(action.navValues).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "COMMITED_FOR_APPROVAL"
            );
            this.store.dispatch(LOAD_PENDING_NAV());
            return of(tradingActions.LOAD_FUND_LIST());
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.store.dispatch(
              tradingActions.SET_TRADING_LOADING_STATUS({ loading: false })
            );
            this.notificationService.show(
              ToasterSeverities.Error,
              "NAV_VALUES_SUBMIT_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadYielduEfficiency$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_YIELDU_EFFICIENCY),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: true }));
        return this.tradingService.getYielduEfficiency$().pipe(
          switchMap((list: Array<Yieldu>): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of(
              tradingActions.SET_YIELDU_EFFICIENCY({ yielduList: list })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(SET_TRADING_LOADING_STATUS({ loading: false }));
            return of();
          })
        );
      })
    );
  });

  saveYielduEfficiency$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.SAVE_YIELDU_EFFICIENCY),
      concatMap((action: { savedData: Array<Yieldu> }): Observable<Action> => {
        return this.tradingService.saveYielduEfficiency$(action.savedData).pipe(
          switchMap((): Observable<Action> => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "YIELDU_COMITTED_FOR_CONFIRM"
            );
            return of(tradingActions.LOAD_YIELDU_EFFICIENCY());
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "YIELDU_SUBMIT_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadTradeStatus$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(tradingActions.LOAD_TRADE_STATUS),
      concatMap((): Observable<Action> => {
        return this.tradingService.checkTradeStatus$().pipe(
          switchMap((tradeStatus: boolean): Observable<Action> => {
            if (tradeStatus) {
              return of(
                tradingActions.SET_TRADE_STATUS({ tradeStatus }),
                tradingActions.SET_TRADE_BLOCKERS({ tradeBlockers: [] })
              );
            }
            // Why the button is disabled (TL-1195). The status stands even if the reasons can't be read.
            return this.tradingService.getTradingBlockers$().pipe(
              catchError(() => of(new Array<string>())),
              switchMap((tradeBlockers: Array<string>) =>
                of(
                  tradingActions.SET_TRADE_STATUS({ tradeStatus }),
                  tradingActions.SET_TRADE_BLOCKERS({ tradeBlockers })
                )
              )
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  /**
   * Polls the latest trade moment until its definitive flag matches the state the trade action is
   * expected to produce, the job fails, or the attempts run out. Emits the last response either way,
   * so a job that outlives the polling window leaves the panel on the state it could confirm.
   */
  private pollTradeMoment$(
    definitive: boolean,
    previousRun: TradingRun | null
  ): Observable<{ tradeMoment: TradeMoment; failedRun: TradingRun | null }> {
    return range(0, TradingEffects.TRADE_MOMENT_POLL_ATTEMPTS).pipe(
      concatMap((attempt: number) =>
        timer(TradingEffects.tradeMomentPollDelay(attempt)).pipe(
          concatMap(() =>
            forkJoin({
              tradeMoment: this.tradingService.getLatestTrading$(false),
              run: this.lastTradingRun$(),
            })
          ),
          map(({ run, tradeMoment }) => ({
            tradeMoment,
            failedRun: TradingEffects.isNewFailure(run, previousRun)
              ? run
              : null,
          }))
        )
      ),
      takeWhile(
        ({ failedRun, tradeMoment }): boolean =>
          !failedRun && tradeMoment?.definitive !== definitive,
        true
      ),
      last()
    );
  }

  /** The latest trading job, or null when it can't be read; polling then falls back to the trade moment. */
  private lastTradingRun$(): Observable<TradingRun | null> {
    return this.tradingService
      .getLastTradingRun$()
      .pipe(catchError(() => of(null)));
  }

  private static isNewFailure(
    run: TradingRun | null,
    previousRun: TradingRun | null
  ): boolean {
    return (
      run?.state === "Failed" &&
      (run.at !== previousRun?.at || run.job !== previousRun?.job)
    );
  }

  /**
   * Backs the poll delay off towards its upper bound, so a job that runs for minutes costs a
   * handful of requests instead of one every few seconds.
   */
  private static tradeMomentPollDelay(attempt: number): number {
    return Math.min(
      TradingEffects.TRADE_MOMENT_POLL_START_INTERVAL * Math.pow(1.5, attempt),
      TradingEffects.TRADE_MOMENT_POLL_MAX_INTERVAL
    );
  }

  constructor(
    private actions$: Actions,
    private tradingService: TradingService,
    private store: Store,
    private notificationService: NotificationService,
    private dialog: DialogHelper
  ) {}
}
